import React from "react";
import { useParams, Link } from "react-router-dom";
import { projects } from "../mock";

export default function ProjectDetail() {
  const { id } = useParams();
  const project = projects.find((p) => p.id === id);

  if (!project) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white px-6">
        <h1 className="text-2xl font-semibold text-gray-900 mb-4">Project not found</h1>
        <Link
          to="/"
          className="text-sm text-gray-500 hover:text-gray-900 transition-colors"
        >
          ← Back to home
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Back button */}
      <div className="max-w-3xl mx-auto px-6 pt-10">
        <Link
          to="/"
          className="inline-flex items-center text-sm text-gray-400 hover:text-gray-900 transition-colors mb-10"
        >
          <svg
            className="w-4 h-4 mr-1.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back to home
        </Link>
      </div>

      {/* Hero image/video */}
      <div className="max-w-4xl mx-auto px-6 mb-10">
        {project.video ? (
          <video
            src={project.video}
            autoPlay
            muted
            loop
            playsInline
            controls
            className="w-full aspect-video object-contain rounded-lg bg-gray-950"
            poster={project.thumbnail}
          />
        ) : (
          <img
            src={project.thumbnail}
            alt={project.title}
            className="w-full aspect-[16/10] object-cover rounded-lg"
          />
        )}
        {project.videoCaption && (
          <p className="mt-3 text-sm text-gray-400 italic text-center">
            {project.videoCaption}
          </p>
        )}
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 pb-20">
        {/* Title */}
        <h1 className="text-3xl font-semibold text-gray-900 mb-2">
          {project.title}
        </h1>

        {/* One-sentence summary */}
        <p className="text-base text-gray-500 mb-6">
          {project.oneLiner || project.summary}
        </p>

        {/* Meta row */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-400 mb-4">
          {project.role && <span>{project.role}</span>}
          {project.role && project.dates && <span>·</span>}
          {project.dates && <span>{project.dates}</span>}
        </div>

        {/* Links (GitHub, etc.) */}
        {project.links && project.links.length > 0 && (
          <div className="flex flex-wrap gap-3 mb-10">
            {project.links.map((link, i) => (
              <a
                key={i}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-sm text-gray-700 bg-gray-50 border border-gray-200 px-4 py-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                {link.label}
                <svg className="w-3.5 h-3.5 ml-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            ))}
          </div>
        )}

        {/* Problem / Goal */}
        {project.problem && (
          <div className="mb-10">
            <h2 className="text-lg font-medium text-gray-900 mb-3">The Problem</h2>
            <p className="text-base text-gray-600 leading-relaxed">
              {project.problem}
            </p>
          </div>
        )}

        {/* Key Design Decisions */}
        {project.designDecisions && project.designDecisions.length > 0 && (
          <div className="mb-10">
            <h2 className="text-lg font-medium text-gray-900 mb-3">Key Design Decisions</h2>
            <ul className="space-y-3">
              {project.designDecisions.map((decision, i) => (
                <li key={i} className="text-sm text-gray-600 leading-relaxed pl-4 border-l-2 border-gray-200">
                  {decision}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* What Went Wrong / Iteration */}
        {project.iterations && project.iterations.length > 0 && (
          <div className="mb-10">
            <h2 className="text-lg font-medium text-gray-900 mb-3">What Went Wrong & Iteration</h2>
            <ul className="space-y-3">
              {project.iterations.map((item, i) => (
                <li key={i} className="text-sm text-gray-600 leading-relaxed pl-4 border-l-2 border-amber-200">
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Specs & Tools */}
        {project.tools && project.tools.length > 0 && (
          <div className="mb-10">
            <h2 className="text-lg font-medium text-gray-900 mb-3">Specs & Tools</h2>
            <ul className="grid grid-cols-2 gap-x-6 gap-y-1.5">
              {project.tools.map((tool, i) => (
                <li key={i} className="text-sm text-gray-600">
                  • {tool}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Results */}
        {project.results && (
          <div className="mb-10">
            <h2 className="text-lg font-medium text-gray-900 mb-3">Results</h2>
            {typeof project.results === "string" ? (
              <p className="text-base text-gray-600 leading-relaxed">{project.results}</p>
            ) : (
              <ul className="space-y-2">
                {project.results.map((r, i) => (
                  <li key={i} className="text-sm text-gray-600 leading-relaxed">
                    • {r}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Result images */}
        {project.resultImages && project.resultImages.length > 0 && (
          <div className="mb-10 grid grid-cols-1 md:grid-cols-2 gap-4">
            {project.resultImages.map((img, i) => (
              <img
                key={i}
                src={img.src}
                alt={img.alt || "Result"}
                className="w-full rounded-lg border border-gray-100"
              />
            ))}
          </div>
        )}

        {/* Future Work */}
        {project.futureWork && project.futureWork.length > 0 && (
          <div className="mb-10">
            <h2 className="text-lg font-medium text-gray-900 mb-3">Future Work</h2>
            <ul className="space-y-2">
              {project.futureWork.map((item, i) => (
                <li key={i} className="text-sm text-gray-600 leading-relaxed">
                  • {item}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Team Note */}
        {project.teamNote && (
          <div className="mb-10">
            <p className="text-sm text-gray-500 italic border-l-2 border-gray-200 pl-4">
              {project.teamNote}
            </p>
          </div>
        )}

        {/* Bottom back link */}
        <div className="pt-6 border-t border-gray-100">
          <Link
            to="/"
            className="inline-flex items-center text-sm text-gray-400 hover:text-gray-900 transition-colors"
          >
            <svg
              className="w-4 h-4 mr-1.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to all projects
          </Link>
        </div>
      </div>
    </div>
  );
}
